import { Component, OnDestroy, OnInit } from '@angular/core';
import { CoreService } from './user/shared/services/core-service.service';
import { JwtTokenService } from './core/shared/jwt-token.service';
import { SessionService } from './user/shared/services/session.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthTokenService } from './core/shared/auth-token.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'avs-portal-frontend';
  private expirationCheckInterval: any;
  private destroy$ = new Subject<void>();

  // Debug-Properties (optional, für Development)
  activeTabCount = 0;
  currentTabId = '';
  isLoggedIn = false;

  constructor(
    private coreService: CoreService,
    private jwtTokenService: JwtTokenService,
    private authTokenService: AuthTokenService,
    private sessionService: SessionService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // ============================================
    // 1. Session Service initialisieren
    // ============================================
    // Nur wenn User bereits eingeloggt ist
    const token = this.jwtTokenService.getJwtToken();
    if (token && this.jwtTokenService.isTokenValid(token)) {
      this.sessionService.initializeSession();
    }

    // ============================================
    // 2. Session Service Subscriptions
    // ============================================
    
    // Überwache Session-Ablauf (15 Min. Inaktivität)
    this.sessionService.getSessionExpired$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
/*         console.log('❌ Session abgelaufen - User wird ausgeloggt');
 */        this.handleSessionExpired();
      });

    // Überwache Login-Status
    this.sessionService.isLoggedIn()
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoggedIn => {
        this.isLoggedIn = isLoggedIn;
/*         console.log(`🔐 Login Status: ${isLoggedIn ? 'Eingeloggt' : 'Ausgeloggt'}`);
 */      });

    // Überwache Anzahl aktiver Tabs (optional für Debug)
    this.sessionService.getActiveTabCount$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.activeTabCount = count;
/*         console.log(`📊 Aktive Tabs: ${count}`);
 */      });

    // Tab-ID für Debug
    this.currentTabId = this.sessionService.getTabId();

    // ============================================
    // 3. Proaktiver Token-Refresh (wie bisher)
    // ============================================
    // Alle 30 Sekunden prüfen, ob Token bald abläuft
    this.expirationCheckInterval = setInterval(() => {
      this.checkTokenExpiration();
    }, 30000);
  }

  /**
   * Prüft Token-Ablauf und refresht proaktiv
   * (Wie bisher - unabhängig vom Session-Management)
   */
  private checkTokenExpiration(): void {
    const token = this.jwtTokenService.getJwtTokenIfValid();

    if (!token) return;

    // ✅ Proaktiv refreshen, BEVOR Token abläuft
    // z.B. bei 5min verbleibende Zeit refreshen (JWT läuft nach 15min ab)
    if (this.jwtTokenService.isTokenExpired(token) ||
      this.jwtTokenService.isTokenExpiringSoon(token, 5 * 60 * 1000)) {
/*       console.log('🔄 Token expiring soon, refreshing proactively...');
 */
      this.authTokenService.getValidAccessToken().subscribe(
        () => {
/*           console.log('✅ Token refreshed successfully');
 */          // Token wurde erneuert - Session bleibt aktiv
          this.sessionService.initializeSession();
        },
        (error) => {
          console.error('❌ Token refresh failed:', error);
        }
      );
    }
  }

  /**
   * Wird aufgerufen wenn Session durch Inaktivität abläuft
   */
  private handleSessionExpired(): void {
/*     console.log('⏱️ Session wegen Inaktivität abgelaufen');
 */    
    // Tokens löschen
    this.jwtTokenService.clearTokens();
    
    // Optional: Benutzer informieren
    // this.showSessionExpiredDialog();
    
    // Zum Login navigieren
    this.router.navigate(['/login'], {
      queryParams: { reason: 'session_expired' }
    });
  }

  /**
   * Optional: Dialog für Session-Ablauf
   */
  private showSessionExpiredDialog(): void {
    // Implementiere einen Dialog mit Angular Material oder eigene Lösung
    alert('Ihre Session ist nach 15 Minuten Inaktivität abgelaufen. Bitte melden Sie sich erneut an.');
  }

  ngOnDestroy(): void {
    // Cleanup Interval
    if (this.expirationCheckInterval) {
      clearInterval(this.expirationCheckInterval);
    }

    // Complete Subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }
}
